// Stable Auth Values Hook - Prevents re-renders when auth store updates but values don't change
import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '@stores/authStore';
import type { AuthUser } from '@lib/auth/types';

interface StableAuthValues {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** True sobald User da ist (Plan kommt synchron aus JWT – kein Delay nötig) */
  planReady: boolean;
}

export const useStableAuthValues = (): StableAuthValues => {
  const [state, setState] = useState<StableAuthValues>(() => {
    const storeState = useAuthStore.getState();
    const hasUser = !!storeState.user?.id;
    return {
      user: storeState.user,
      isLoading: storeState.isLoading,
      isAuthenticated: storeState.isAuthenticated,
      planReady: hasUser,
    };
  });

  const prevUserRef = useRef(state.user);
  const prevIsLoadingRef = useRef(state.isLoading);
  const prevIsAuthenticatedRef = useRef(state.isAuthenticated);
  
  useEffect(() => {
    const unsubscribe = useAuthStore.subscribe((storeState) => {
      const userChanged = storeState.user?.id !== prevUserRef.current?.id || 
                          storeState.user?.plan !== prevUserRef.current?.plan ||
                          storeState.user?.storageUsed !== prevUserRef.current?.storageUsed ||
                          storeState.user?.storageLimit !== prevUserRef.current?.storageLimit ||
                          storeState.user?.isSuspended !== prevUserRef.current?.isSuspended;
      const isLoadingChanged = storeState.isLoading !== prevIsLoadingRef.current;
      const isAuthenticatedChanged = storeState.isAuthenticated !== prevIsAuthenticatedRef.current;
      
      if (userChanged || isLoadingChanged || isAuthenticatedChanged) {
        if (userChanged) prevUserRef.current = storeState.user;
        if (isLoadingChanged) prevIsLoadingRef.current = storeState.isLoading;
        if (isAuthenticatedChanged) prevIsAuthenticatedRef.current = storeState.isAuthenticated;
        const hasUser = !!storeState.user?.id;
        setState((prev) => ({
          ...prev,
          user: storeState.user,
          isLoading: storeState.isLoading,
          isAuthenticated: storeState.isAuthenticated,
          planReady: hasUser,
        }));
      }
    });
    return unsubscribe;
  }, []);
  
  return { ...state, planReady: state.planReady ?? !!state.user?.id };
};
